import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductReviews, submitProductReview } from '../api/api';
import { useAuth } from '../context/AuthContext';
import './ProductReviews.css';

const StarRating = ({ value, interactive = false, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const display = interactive ? (hovered || value) : value;

  return (
    <div className={`star-rating ${interactive ? 'interactive' : 'static'}`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`star ${star <= display ? 'filled' : 'empty'}`}
          onClick={() => interactive && onChange && onChange(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          role={interactive ? 'button' : undefined}
          aria-label={interactive ? `Rate ${star} out of 5` : undefined}
        >
          ★
        </span>
      ))}
    </div>
  );
};

const RatingBar = ({ label, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="rating-bar-row">
      <span className="rating-bar-label">{label}</span>
      <div className="rating-bar-track">
        <div className="rating-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="rating-bar-count">{count}</span>
    </div>
  );
};

function ProductReviews({ productId }) {
  const navigate = useNavigate();
  const { user, isShopper } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const fetchReviews = useCallback(async () => {
    if (!productId) return;
    try {
      setLoading(true);
      const res = await getProductReviews(productId);
      const data = res.data;
      setReviews(data.reviews || []);
      setAvgRating(data.avgRating || 0);
    } catch (err) {
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (rating === 0) {
      setError('Please select a star rating.');
      return;
    }
    if (comment.trim().length < 3) {
      setError('Please write at least a short comment.');
      return;
    }

    try {
      setSubmitting(true);
      await submitProductReview(productId, { rating, comment });
      setSuccess('Your review has been submitted. Thank you!');
      setRating(0);
      setComment('');
      await fetchReviews();
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not submit your review. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Distribution breakdown
  const distribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length
  }));

  const alreadyReviewed = user && reviews.some(
    (r) => r.userId === (user._id || user.id || user.uid)
  );

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-ZA', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="product-reviews">
      <h2 className="reviews-title">Customer Reviews</h2>

      {loading ? (
        <div className="reviews-loading">Loading reviews…</div>
      ) : (
        <>
          {/* Summary bar */}
          <div className="reviews-summary">
            <div className="reviews-avg-block">
              <span className="reviews-avg-number">{avgRating > 0 ? avgRating.toFixed(1) : '—'}</span>
              <StarRating value={Math.round(avgRating)} />
              <span className="reviews-count">
                {reviews.length === 0
                  ? 'No reviews yet'
                  : `${reviews.length} review${reviews.length !== 1 ? 's' : ''}`}
              </span>
            </div>
            <div className="reviews-distribution">
              {distribution.map(({ star, count }) => (
                <RatingBar key={star} label={`${star}★`} count={count} total={reviews.length} />
              ))}
            </div>
          </div>

          {/* Review form */}
          <div className="reviews-form-section">
            {isShopper() ? (
              alreadyReviewed ? (
                <p className="already-reviewed">You have already reviewed this product.</p>
              ) : (
                <form className="review-form" onSubmit={handleSubmit}>
                  <h3>Write a Review</h3>
                  <div className="form-group">
                    <label>Your Rating</label>
                    <StarRating value={rating} interactive onChange={setRating} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="review-comment">Your Review</label>
                    <textarea
                      id="review-comment"
                      rows={4}
                      placeholder="Share your thoughts about this product…"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      maxLength={1000}
                    />
                    <span className="char-count">{comment.length}/1000</span>
                  </div>
                  {error && <p className="review-error">{error}</p>}
                  {success && <p className="review-success">{success}</p>}
                  <button
                    type="submit"
                    className="submit-review-btn"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting…' : 'Submit Review'}
                  </button>
                </form>
              )
            ) : (
              <div className="review-signin-prompt">
                <p>Have this product? Share your experience!</p>
                <button
                  className="signin-to-review-btn"
                  onClick={() => navigate('/login')}
                >
                  Sign In to Leave a Review
                </button>
              </div>
            )}
          </div>

          {/* Review list */}
          {reviews.length > 0 && (
            <div className="reviews-list">
              {reviews.map((review) => (
                <div key={review._id} className="review-card">
                  <div className="review-header">
                    <div className="reviewer-info">
                      <div className="reviewer-avatar">
                        {(review.userName || 'A')[0].toUpperCase()}
                      </div>
                      <div>
                        <span className="reviewer-name">{review.userName || 'Anonymous'}</span>
                        <span className="review-date">{formatDate(review.createdAt)}</span>
                      </div>
                    </div>
                    <StarRating value={review.rating} />
                  </div>
                  <p className="review-comment">{review.comment}</p>
                </div>
              ))}
            </div>
          )}

          {reviews.length === 0 && !isShopper() && (
            <p className="no-reviews-yet">Be the first to review this product!</p>
          )}
        </>
      )}
    </div>
  );
}

export default ProductReviews;
